import React, { useMemo } from "react";
import { Grid } from "@material-ui/core";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { Controller } from "react-hook-form";
import { EventNotification } from "../../domain/EventNotification";
import { Error } from "../common/Error";
import { SectionProps } from "./SectionProps";
import { UserType } from "../../redux/types";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { aoServiceRefData } from "../../services";
import { fieldLabels } from "./fieldLabels";

export const EventDetails: React.FC<SectionProps<EventNotification>> = ({
  user: { token, userType },
  control,
  errors,
  disabled,
  setValue,
  isCreateMode,
  lookupTracker,
}) => {
  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoServiceRefData.get(id.toString(), { token }),
      search: (params?: QueryParams) => {
        const queryParams: QueryParams = {
          ...params,
          search: params?.search || params?.query,
        };
        return aoServiceRefData.search(queryParams, { token });
      },
    }),
    [token]
  );

  return (
    <Grid container spacing={3}>
      {userType === UserType.Ofqual || userType === UserType.CCEA ? (
        <Grid item xs={12}>
          <Controller
            control={control}
            rules={{ required: true }}
            name="orgId"
            render={({ onChange, ...props }) => (
              <SearchField
                {...props}
                onChange={(e: any) => {
                  onChange(e);
                }}
                searchFieldVariant="single"
                label={fieldLabels["orgId"]}
                datatest="orgId"
                service={mappedOrgLookupService}
                readOnly={disabled}
                required
                lookupTracker={lookupTracker}
              />
            )}
          />
          {errors.orgId && <Error>This field is required</Error>}
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <Controller
          xs={6}
          control={control}
          name="title"
          rules={{ required: true }}
          as={
            <TextField
              label={fieldLabels["title"]}
              readOnly={disabled}
              required
              multiline
              data-test="title"
            />
          }
        />
        {errors.title && (
          <Error data-test="title-error">This field is required</Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Grid item xs={6}>
          <Controller
            control={control}
            name="dateOfEvent"
            as={
              <DatePicker
                returnDayBoundary="endOfDay"
                label={fieldLabels["dateOfEvent"]}
                readOnly={disabled}
                dataTest="date-of-event"
              />
            }
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid item xs={12} sm={8}>
          <Controller
            control={control}
            name="dateAware"
            as={
              <DatePicker
                returnDayBoundary="endOfDay"
                label={fieldLabels["dateAware"]}
                readOnly={disabled}
                dataTest="date-aware"
              />
            }
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="reasonForDelayInNotifying"
          as={
            <TextField
              label={fieldLabels["reasonForDelayInNotifying"]}
              readOnly={disabled}
              multiline
              data-test="reason-for-delay"
            />
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="aoReference"
          rules={{ maxLength: 500 }}
          as={
            <TextField
              label={fieldLabels["aoReference"]}
              readOnly={disabled}
              multiline
              data-test="ao-reference"
            />
          }
        />
        {errors.aoReference && (
          <Error data-test="aoReference-error">
            AO Reference cannot be longer than 500 characters
          </Error>
        )}
      </Grid>
    </Grid>
  );
};
